import React from 'react';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
  Box,
  IconButton,
  useTheme,
  accordionSummaryClasses,
  Stack,
  Tooltip,
  SxProps,
  Theme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import TriangleRightIcon from '../icons/TriangleRightIcon';

export interface CollapsibleSectionProps {
  title: string | React.ReactNode;
  subTitle?: string | React.ReactNode;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  explanation?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  subTitle,
  defaultOpen = true,
  action,
  children,
  sx,
  explanation,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultOpen);

  const handleChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <MuiAccordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      onChange={handleChange}
      sx={{
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: 'none',
        padding: 0,
        '&:before': { display: 'none' },
        ...sx,
      }}
    >
      <MuiAccordionSummary
        expandIcon={
          <IconButton
            size="small"
            disableRipple
            sx={{
              color: 'black',
              padding: 0,
            }}
          >
            <TriangleRightIcon sx={{ fontSize: '1.8rem' }} />
          </IconButton>
        }
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: 0,
          },
          padding: 1,
          flexDirection: 'row-reverse',
          [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
            transform: 'rotate(90deg)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} textAlign="center">
          <Stack direction="row" alignItems="flex-end" gap={1}>
            <Typography variant="h5">{title}</Typography>
            {subTitle && (
              <Typography color="textSecondary" variant="body2">
                {subTitle}
              </Typography>
            )}
          </Stack>

          {explanation && (
            <Tooltip title={explanation} arrow placement="top">
              <InfoOutlinedIcon fontSize="small" color="info" />
            </Tooltip>
          )}
        </Stack>
        {action && (
          <Box onClick={e => e.stopPropagation()} onFocus={e => e.stopPropagation()}>
            {action}
          </Box>
        )}
      </MuiAccordionSummary>

      <MuiAccordionDetails sx={{ pt: 0, pr: 1.5, pb: 1.5, pl: 1.5 }}>{children}</MuiAccordionDetails>
    </MuiAccordion>
  );
};

export default CollapsibleSection;
